import type { TrialStatus } from '$lib/types/ai';
import { HttpClient } from '$lib/utils/httpClient';
import { sha256, generateUUID } from '$lib/utils/crypto';

/**
 * Trial Limits Constants
 */
export const TRIAL_LIMITS = {
  DURATION_DAYS: 14,
  TRIAL_UNLIMITED: true,
} as const;

export const FREE_TIER_LIMITS = {
  DAILY_SPELL_CHECK_LIMIT: 10,
  DAILY_AI_ANALYSIS_LIMIT: 10,
  RESET_HOUR: 0,
} as const;

interface ServerTrialResponse {
  success: boolean;
  trial: {
    deviceId: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
    daysRemaining: number;
    hasExpired: boolean;
    deviceInfo?: any;
    createdAt?: string;
  } | null;
  isNewTrial?: boolean;
  canUseTrial?: boolean;
  isTrialActive?: boolean;
  error?: string;
  message?: string;
}

/**
 * Server-based Trial Management Service
 * Manages trial periods through server API to prevent exploitation
 */
export class ServerTrialService {
  private static instance: ServerTrialService;
  private static API_BASE = import.meta.env.VITE_API_URL || 'https://appstore.junlim.org';
  private static readonly STORAGE_KEY = 'subscription_trial_info';
  private trialInfo: any = null;
  
  private constructor() {
    // Initialize trial info on construction
    this.loadTrialInfo();
  }
  
  static getInstance(): ServerTrialService {
    if (!ServerTrialService.instance) {
      ServerTrialService.instance = new ServerTrialService();
    }
    return ServerTrialService.instance;
  }
  
  /**
   * Get device ID and fingerprint for trial tracking
   */
  private static async getDeviceInfo() {
    // Get device ID directly from localStorage (same as DeviceLimitManagerImpl)
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      // Generate simple device ID
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 15);
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      // Create a simple hash
      const baseString = `${userAgent}-${platform}-${timestamp}`;
      const simpleHash = baseString.split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0);
      }, 0).toString(36);
      
      deviceId = `${randomStr}-${simpleHash}-${timestamp}`;
      localStorage.setItem('device_id', deviceId);
    }
    
    // Generate device fingerprint
    const fingerprintComponents = {
      screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language || navigator.languages?.[0] || 'unknown',
      platform: navigator.platform,
      vendor: navigator.vendor || 'unknown',
      userAgent: navigator.userAgent
    };
    
    // Create fingerprint hash
    const fingerprintString = Object.values(fingerprintComponents).join('|');
    const fingerprint = await ServerTrialService.hashString(fingerprintString);
    
    // Get device info
    const deviceInfo = {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
    
    return { deviceId, fingerprint, deviceInfo };
  }
  
  /**
   * Hash a string using crypto utility with fallback
   */
  private static async hashString(str: string): Promise<string> {
    return sha256(str);
  }
  
  /**
   * Initialize or check trial status from server
   */
  static async initializeTrial(): Promise<TrialStatus> {
    try {
      const { deviceId, fingerprint, deviceInfo } = await this.getDeviceInfo();
      const httpClient = HttpClient.getInstance();
      
      const data = await httpClient.request<ServerTrialResponse>(
        `${this.API_BASE}/api/trial/initialize`,
        {
          method: 'POST',
          headers: {
            'X-Device-ID': deviceId
          },
          body: {
            deviceId,
            deviceFingerprint: fingerprint,
            deviceInfo
          },
          maxRetries: 3,
          retryDelay: 2000
        }
      );
      
      if (!data.success) {
        console.error('[ServerTrialService] Failed to initialize trial:', data.error || data.message);
        
        // If trial was already used, return expired trial
        if (data.error === 'trial_already_used') {
          console.warn('[ServerTrialService] Trial already used on this device');
          return {
            isActive: false,
            startDate: '',
            endDate: '',
            daysRemaining: 0,
            hasExpired: true,
            isFreeTier: true,
            aiUsageCount: 0,
            aiDailyLimit: FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT,
            spellCheckUsageCount: 0,
            spellCheckDailyLimit: FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT
          };
        }
        
        // For other errors, fall back to local storage
        return this.getLocalTrialStatus();
      }
      
      if (!data.trial) {
        // No trial data returned
        return this.getDefaultTrialStatus();
      }
      
      // Convert server response to TrialStatus
      const trialStatus: TrialStatus = {
        isActive: data.trial.isActive && !data.trial.hasExpired,
        startDate: data.trial.startDate,
        endDate: data.trial.endDate,
        daysRemaining: data.trial.daysRemaining,
        hasExpired: data.trial.hasExpired,
        isFreeTier: false,
        aiUsageCount: 0,
        aiDailyLimit: data.trial.hasExpired ? FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT : Infinity,
        spellCheckUsageCount: 0,
        spellCheckDailyLimit: data.trial.hasExpired ? FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT : Infinity
      };
      
      // Cache in localStorage for offline access
      this.saveLocalTrialStatus(trialStatus);
      
      return trialStatus;
      
    } catch (error: any) {
      console.error('[ServerTrialService] Error initializing trial:', error);
      
      // If trial was already used (403), clear local cache and return expired trial
      if (error.status === 403 || error.message?.includes('trial_already_used')) {
        console.warn('[ServerTrialService] Trial already used on this device');
        // Clear local cache to ensure fresh check next time
        localStorage.removeItem(this.STORAGE_KEY);
        
        const expiredTrial = {
          isActive: false,
          startDate: '',
          endDate: '',
          daysRemaining: 0,
          hasExpired: true,
          isFreeTier: true,
          aiUsageCount: 0,
          aiDailyLimit: FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT,
          spellCheckUsageCount: 0,
          spellCheckDailyLimit: FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT
        };
        
        // Save expired status to prevent repeated server checks
        this.saveLocalTrialStatus(expiredTrial);
        return expiredTrial;
      }
      
      // Fall back to local storage on network error
      return this.getLocalTrialStatus();
    }
  }
  
  /**
   * Check trial status from server
   */
  static async checkTrialStatus(): Promise<TrialStatus> {
    try {
      const { deviceId } = await this.getDeviceInfo();
      const httpClient = HttpClient.getInstance();
      
      const data = await httpClient.request<ServerTrialResponse>(
        `${this.API_BASE}/api/trial/status/${deviceId}`,
        {
          method: 'GET',
          headers: {
            'X-Device-ID': deviceId
          },
          maxRetries: 3,
          retryDelay: 2000
        }
      );
      
      if (!data.success) {
        console.error('[ServerTrialService] Failed to check trial status:', data.error || data.message);
        return this.getLocalTrialStatus();
      }
      
      if (!data.trial) {
        // No trial exists, can start new one
        return this.initializeTrial();
      }
      
      // Convert server response to TrialStatus
      const trialStatus: TrialStatus = {
        isActive: data.trial.isActive && !data.trial.hasExpired,
        startDate: data.trial.startDate,
        endDate: data.trial.endDate,
        daysRemaining: data.trial.daysRemaining,
        hasExpired: data.trial.hasExpired,
        isFreeTier: data.trial.hasExpired,
        aiUsageCount: 0,
        aiDailyLimit: data.trial.hasExpired ? FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT : Infinity,
        spellCheckUsageCount: 0,
        spellCheckDailyLimit: data.trial.hasExpired ? FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT : Infinity
      };
      
      // Cache in localStorage
      this.saveLocalTrialStatus(trialStatus);
      
      return trialStatus;
      
    } catch (error) {
      console.error('[ServerTrialService] Error checking trial status:', error);
      return this.getLocalTrialStatus();
    }
  }
  
  /**
   * Verify if device can use trial features
   */
  static async verifyTrial(): Promise<boolean> {
    try {
      const { deviceId } = await this.getDeviceInfo();
      const httpClient = HttpClient.getInstance();
      
      const data = await httpClient.request<ServerTrialResponse>(
        `${this.API_BASE}/api/trial/verify`,
        {
          method: 'POST',
          headers: {
            'X-Device-ID': deviceId
          },
          body: { deviceId },
          maxRetries: 3,
          retryDelay: 2000
        }
      );
      
      if (!data.success) {
        console.error('[ServerTrialService] Failed to verify trial:', data.error || data.message);
        return false;
      }
      
      return data.canUseTrial || false;
      
    } catch (error) {
      console.error('[ServerTrialService] Error verifying trial:', error);
      // Check local status as fallback
      const localStatus = this.getLocalTrialStatus();
      return localStatus.isActive && !localStatus.hasExpired;
    }
  }
  
  /**
   * Get local trial status from localStorage (fallback)
   */
  private static getLocalTrialStatus(): TrialStatus {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    
    if (!stored) {
      return this.getDefaultTrialStatus();
    }
    
    try {
      const trial = JSON.parse(stored);
      // Recalculate days remaining
      if (trial.endDate) {
        const now = new Date();
        const endDate = new Date(trial.endDate);
        const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        trial.daysRemaining = Math.max(0, daysRemaining);
        trial.hasExpired = daysRemaining <= 0;
        trial.isActive = !trial.hasExpired;
      }
      return trial;
    } catch (error) {
      console.error('[ServerTrialService] Error parsing local trial status:', error);
      return this.getDefaultTrialStatus();
    }
  }
  
  /**
   * Save trial status to localStorage (for offline access)
   */
  private static saveLocalTrialStatus(trial: TrialStatus): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trial));
    } catch (error) {
      console.error('[ServerTrialService] Error saving local trial status:', error);
    }
  }
  
  /**
   * Get default trial status (no trial)
   */
  private static getDefaultTrialStatus(): TrialStatus {
    return {
      isActive: false,
      startDate: '',
      endDate: '',
      daysRemaining: 0,
      hasExpired: true,
      isFreeTier: true,
      aiUsageCount: 0,
      aiDailyLimit: FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT,
      spellCheckUsageCount: 0,
      spellCheckDailyLimit: FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT
    };
  }
  
  /**
   * Check free tier usage limits
   */
  static checkFreeTierUsage(trial: TrialStatus): TrialStatus {
    // Reset daily counts if needed
    const today = new Date().toDateString();
    if (trial.lastResetDate !== today) {
      return {
        ...trial,
        spellCheckUsageCount: 0,
        aiUsageCount: 0,
        lastResetDate: today
      };
    }
    
    return trial;
  }
  
  /**
   * Format trial status for display
   */
  static formatTrialStatus(trial: TrialStatus): string {
    if (!trial.isActive && trial.isFreeTier) {
      return `무료 플랜 (일일 한도: AI ${FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT}회, 맞춤법 ${FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT}회)`;
    }
    
    if (trial.hasExpired) {
      return '체험 기간이 종료되었습니다';
    }
    
    if ((trial.daysRemaining || 0) <= 3) {
      return `체험 기간 ${trial.daysRemaining || 0}일 남음`;
    }
    
    return `체험 기간 (${trial.daysRemaining || 0}일 남음)`;
  }
  
  /**
   * Get trial progress percentage
   */
  static getTrialProgress(trial: TrialStatus): number {
    if (!trial.startDate || !trial.endDate) return 0;
    
    const start = new Date(trial.startDate).getTime();
    const end = new Date(trial.endDate).getTime();
    const now = new Date().getTime();
    
    const total = end - start;
    const elapsed = now - start;
    
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  }
  
  /**
   * Check if should prompt for upgrade
   */
  static shouldPromptUpgrade(trial: TrialStatus): boolean {
    // Prompt when trial has 3 days or less remaining
    return trial.isActive && (trial.daysRemaining || 0) <= 3 && (trial.daysRemaining || 0) > 0;
  }
  
  /**
   * End trial early (for testing)
   */
  static async endTrial(): Promise<TrialStatus> {
    const trial = await this.checkTrialStatus();
    const endedTrial: TrialStatus = {
      ...trial,
      isActive: false,
      hasExpired: true,
      isFreeTier: true,
      daysRemaining: 0,
      aiDailyLimit: FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT,
      spellCheckDailyLimit: FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT,
      aiUsageCount: 0,
      spellCheckUsageCount: 0,
      lastResetDate: new Date().toDateString()
    };
    
    this.saveLocalTrialStatus(endedTrial);
    return endedTrial;
  }
  
  /**
   * Reset trial (for testing in development)
   */
  static async resetTrial(): Promise<TrialStatus> {
    if (import.meta.env.DEV) {
      localStorage.removeItem(this.STORAGE_KEY);
      return this.initializeTrial();
    }
    throw new Error('Trial reset is only available in development mode');
  }
  
  // ===== Instance methods for compatibility with existing TrialService =====
  
  /**
   * Load trial info from localStorage
   */
  private loadTrialInfo(): any {
    const stored = localStorage.getItem(ServerTrialService.STORAGE_KEY);
    if (stored) {
      try {
        this.trialInfo = JSON.parse(stored);
        return this.trialInfo;
      } catch (error) {
        console.error('Error loading trial info:', error);
      }
    }
    return null;
  }
  
  /**
   * Initialize trial (instance method for compatibility)
   */
  async initializeTrial(): Promise<any> {
    const trialStatus = await ServerTrialService.initializeTrial();
    
    // Convert TrialStatus to TrialInfo format for compatibility
    this.trialInfo = {
      isActive: trialStatus.isActive,
      startDate: new Date(trialStatus.startDate || new Date()),
      endDate: new Date(trialStatus.endDate || new Date()),
      daysRemaining: trialStatus.daysRemaining,
      hasExpired: trialStatus.hasExpired
    };
    
    return this.trialInfo;
  }
  
  /**
   * Get trial info (instance method for compatibility)
   */
  getTrialInfo(): any {
    // Always reload from localStorage to get latest updates
    this.loadTrialInfo();
    
    // If still no trial info, return null (indicates no trial)
    if (!this.trialInfo) {
      return null;
    }
    
    // Update days remaining and expiration status
    const now = new Date();
    const endDate = new Date(this.trialInfo.endDate);
    const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    
    this.trialInfo.daysRemaining = daysRemaining;
    this.trialInfo.hasExpired = daysRemaining === 0;
    this.trialInfo.isActive = daysRemaining > 0;
    
    return this.trialInfo;
  }
  
  /**
   * Check if trial has expired
   */
  hasExpired(): boolean {
    const info = this.getTrialInfo();
    return info ? info.hasExpired : true;
  }
  
  /**
   * Check if trial is active
   */
  isActive(): boolean {
    const info = this.getTrialInfo();
    return info ? info.isActive : false;
  }
  
  /**
   * Get days remaining in trial
   */
  getDaysRemaining(): number {
    const info = this.getTrialInfo();
    return info ? info.daysRemaining : 0;
  }
  
  /**
   * Check if user has ever had a trial
   */
  hasHadTrial(): boolean {
    return this.loadTrialInfo() !== null;
  }
  
  /**
   * End trial (instance method for compatibility)
   */
  endTrial(): void {
    if (this.trialInfo) {
      this.trialInfo.isActive = false;
      this.trialInfo.hasExpired = true;
      this.trialInfo.daysRemaining = 0;
      
      // Save to localStorage
      localStorage.setItem(ServerTrialService.STORAGE_KEY, JSON.stringify(this.trialInfo));
    }
  }
  
  /**
   * Extend trial by days (for testing)
   */
  extendTrial(days: number): void {
    if (this.trialInfo && import.meta.env.DEV) {
      const endDate = new Date(this.trialInfo.endDate);
      endDate.setDate(endDate.getDate() + days);
      this.trialInfo.endDate = endDate;
      
      // Recalculate status
      const now = new Date();
      const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
      
      this.trialInfo.daysRemaining = daysRemaining;
      this.trialInfo.hasExpired = daysRemaining === 0;
      this.trialInfo.isActive = daysRemaining > 0;
      
      // Save to localStorage
      localStorage.setItem(ServerTrialService.STORAGE_KEY, JSON.stringify(this.trialInfo));
    }
  }
  
  /**
   * Reset trial (instance method for development)
   */
  resetTrial(): void {
    if (import.meta.env.DEV) {
      localStorage.removeItem(ServerTrialService.STORAGE_KEY);
      this.trialInfo = null;
      this.initializeTrial();
    }
  }
}

// Export default for easier imports
export default ServerTrialService;